import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Panel, SectionTitle, TactLoader } from "../components/Tactical";
import SendReminderButton from "../components/SendReminderButton";
import { useT } from "../context/LanguageContext";
import { Calendar, AlertTriangle, Bell, Bot, ShieldAlert } from "lucide-react";

function whenAgo(iso) {
  if (!iso) return "—";
  try {
    const ms = Date.now() - new Date(iso).getTime();
    if (ms < 60_000) return `${Math.max(1, Math.floor(ms / 1000))}s ago`;
    if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
    if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
    return `${Math.floor(ms / 86_400_000)}d ago`;
  } catch {
    return iso;
  }
}

const LEVEL_COLORS = {
  0: { color: "#71717A", bg: "transparent", icon: Bell, key: "rem.lastNever" },
  1: { color: "#7C3AED", bg: "rgba(124,58,237,0.06)", icon: Bell, key: "rem.level1" },
  2: { color: "#EA580C", bg: "rgba(234,88,12,0.06)", icon: AlertTriangle, key: "rem.level2" },
  3: { color: "#E11D48", bg: "rgba(225,29,72,0.06)", icon: ShieldAlert, key: "rem.level3" },
};

export default function MissingSubmissionsTab() {
  const { t } = useT();
  const [rows, setRows] = useState(null);

  const refresh = async () => {
    try {
      const { data } = await api.get("/admin/missing-submissions");
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setRows([]);
    }
  };
  useEffect(() => {
    refresh();
  }, []);

  return (
    <div className="space-y-6" data-testid="missing-submissions-tab">
      <SectionTitle kicker={t("rem.kicker")} testId="reminders-title">{t("rem.title")}</SectionTitle>
      <p className="font-mono text-xs text-[#71717A] -mt-2">{t("rem.subtitle")}</p>

      {rows === null ? (
        <TactLoader />
      ) : rows.length === 0 ? (
        <Panel className="font-mono text-[#71717A] text-sm" testId="reminders-empty">
          {t("rem.empty")}
        </Panel>
      ) : (
        <ul className="space-y-2" data-testid="reminders-list">
          {rows.map((r) => {
            const level = r.last_reminder_level || 0;
            const meta = LEVEL_COLORS[level];
            const LIcon = meta.icon;
            const byAuto = r.last_reminder_by === "auto";
            return (
              <li
                key={`${r.match_id}-${r.user.id}`}
                data-testid={`reminders-row-${r.match_id}-${r.user.id}`}
                className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] border-l-2 px-4 py-3 flex items-center gap-4 flex-wrap"
                style={{ borderLeftColor: meta.color, backgroundColor: level === 3 ? meta.bg : undefined }}
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <LIcon className="w-4 h-4 flex-shrink-0" style={{ color: meta.color }} />
                  <div className="min-w-0">
                    <div className="font-heading text-base uppercase tracking-wide text-[#09090B] dark:text-[#F4F4F5] truncate">
                      <Link to={`/players/${encodeURIComponent(r.user.username || "")}`} className="hover:text-[#7C3AED] transition-colors">
                        {r.user.username}
                      </Link>
                      <span className="text-[#71717A] mx-2 font-mono text-xs">{t("mm.vs")}</span>
                      <span className="text-[#52525B] dark:text-[#A1A1AA]">{r.opponent.username || "—"}</span>
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A] mt-0.5 flex items-center gap-2 flex-wrap">
                      <span><Calendar className="inline w-3 h-3 mr-1" />{(r.scheduled_at || "").slice(0, 10)}</span>
                      {r.days_overdue > 0 ? (
                        <span className="text-[#E11D48]">· {t("rem.daysOverdue", { n: r.days_overdue })}</span>
                      ) : (
                        <span className="text-[#EA580C]">· {t("sb.today")}</span>
                      )}
                      {r.league_name && <span>· {r.league_name}</span>}
                      {typeof r.round !== "undefined" && r.round !== null && <span>· {t("fx.round")} {r.round}</span>}
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] mt-1" style={{ color: meta.color }}>
                      {level === 0 ? t("rem.lastNever") : (
                        <>
                          {t("rem.last", { level, when: whenAgo(r.last_reminder_at) })}
                          {byAuto ? <span className="ml-1.5 inline-flex items-center gap-1 text-[#71717A]"><Bot className="w-3 h-3" /> {t("rem.byAuto")}</span> : <span className="ml-1.5 text-[#71717A]">· {t("rem.byAdmin")}</span>}
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <SendReminderButton
                  matchId={r.match_id}
                  userId={r.user.id}
                  username={r.user.username}
                  currentLevel={level}
                  onSent={refresh}
                />
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
